// FILE: /src/Components/Public/SharedLinkView.js (FULLY UPDATED)

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Spin, Empty, Button, Avatar, Input, message } from 'antd';
import moment from 'moment';
import { LockOutlined, LikeFilled, LikeOutlined, MessageOutlined } from '@ant-design/icons';

import FacebookModal from '../Social Desings/FacebookModal';
import InstagarmModal from '../Social Desings/InstagarmModal';
import Pinerest from '../Social Desings/Pinerest';
import LinkedInModal from '../Social Desings/LinkedInModal';
import YoutubeModal from '../Social Desings/YoutubeModal';
import TikTokModal from '../Social Desings/TikTokModal';

const { TextArea } = Input;

// --- Sub-component: Right-side CTA Panel (Redesigned) ---
const CtaPanel = () => (
    <div className="sticky top-8 bg-white p-8 rounded-2xl shadow-xl border border-gray-200 text-center">
        <h2 className="text-3xl font-extrabold text-gray-900">Oolook</h2>
        <h3 className="font-semibold text-xl text-gray-800 leading-tight mt-6">Streamline Your Social Workflow</h3>
        <p className="text-base text-gray-600 mt-3 mb-8 leading-relaxed">
            Approve, schedule, and publish content across all your channels.
        </p>
        <div className="space-y-4">
            <Link to="/signup">
                <Button type="primary" block size="large" className="font-semibold h-12">Sign Up Free</Button>
            </Link>
            <Link to="/login">
                <Button block size="large" className="font-semibold h-12">Log In</Button>
            </Link>
        </div>
    </div>
);

// --- Sub-component: Password Entry Form (Logo Updated) ---
const PasswordForm = ({ title, onVerify }) => {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault(); setLoading(true);
        try { await onVerify(password); } 
        catch (error) { message.error(error.response?.data?.message || 'Incorrect password.'); } 
        finally { setLoading(false); }
    };
    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-50 p-4">
            <div className="max-w-sm w-full bg-white p-8 rounded-xl shadow-lg border text-center">
                <h2 className="text-2xl font-extrabold text-gray-900">Oolook</h2>
                <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 mb-6">This content is password protected. Please enter the password to view.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Input.Password placeholder="Enter password" prefix={<LockOutlined />} value={password} onChange={(e) => setPassword(e.target.value)} size="large"/>
                    <Button type="primary" htmlType="submit" loading={loading} block size="large">Unlock Content</Button>
                </form>
            </div>
        </div>
    );
};

// --- Sub-component: Individual Post Card with Feedback (Redesigned) ---
const PublicPostCard = ({ post, shareableId, onInteraction, profileDetails }) => {
    const { _id, likes = 0, comments = [] } = post;
    const [isLiked, setIsLiked] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    useEffect(() => {
        const likedPosts = JSON.parse(localStorage.getItem(`oolook_likes_${shareableId}`) || '[]');
        if (likedPosts.includes(_id)) setIsLiked(true);
    }, [_id, shareableId]);
    
    const handleLike = async () => {
        if (isLiked) return;
        setIsLiked(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API}/api/links/public/${shareableId}/posts/${_id}/like`);
            const likedPosts = JSON.parse(localStorage.getItem(`oolook_likes_${shareableId}`) || '[]');
            localStorage.setItem(`oolook_likes_${shareableId}`, JSON.stringify([...likedPosts, _id]));
            onInteraction(_id, 'like');
        } catch (error) {
            if (error.response && error.response.status === 409) console.log("Already liked.");
            else { setIsLiked(false); message.error(error.response?.data?.message || 'Could not like post.'); }
        }
    };

    const handleCommentSubmit = async () => {
        if (!commentText.trim()) return;
        setIsSubmitting(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_API}/api/links/public/${shareableId}/posts/${_id}/comment`, { content: commentText });
            const newComment = response.data;
            setCommentText('');
            message.success('Comment added!');
            onInteraction(_id, 'comment', newComment);
        } catch (error) {
             message.error(error.response?.data?.message || 'Could not add comment.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderPreviewComponent = () => {
        const platform = post.platforms?.[0];
        const profileId = post.selectedProfiles?.[0];
        const detail = profileDetails[profileId] || {};
        const profile = {
            platform: detail.platform, platform_id: profileId, platform_pageId: profileId,
            profile: detail.profile, profile_picture_url: detail.profile_picture_url,
        };
        const mediaItems = (post.media_url || []).map(url => ({
            permanentUrl: url,
            type: /\.(mp4|mov|webm)$/i.test(url) ? 'video' : 'image'
        }));
        switch (platform) {
            case 'FACEBOOK': return <FacebookModal Previewimages={mediaItems} Previewcaptions={post.message} profile={profile} />;
            case 'INSTAGRAM': return <InstagarmModal Previewimages={mediaItems} Previewcaptions={post.message} profile={profile} instagramOptions={{ firstComment: post.first_comment }} />;
            case 'PINTEREST': return <Pinerest Previewimages={mediaItems} pinTitle={post.title} pinDescription={post.message} profile={profile} thumbnailUrl={post.pinterest_thumbnail_url} />;
            case 'LINKEDIN': return <LinkedInModal mediaItems={mediaItems} captions={post.message} profile={profile} linkedinOptions={post.platformOptions?.linkedinOptions || {}} />;
            case 'YOUTUBE': return <YoutubeModal Previewimages={mediaItems} Previewcaptions={post.message} profile={profile} title={post.platformOptions?.YOUTUBE?.[profileId]?.title || post.title} />;
            case 'TIKTOK': return <TikTokModal Previewimages={mediaItems} Previewcaptions={post.message} profile={profile} />;
            default: return <div className="p-4 text-center text-gray-500">Preview for this platform is not available.</div>;
        }
    };

    return (
        <div className="flex flex-col bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden max-w-xl mx-auto">
            {renderPreviewComponent()}
            <div className="bg-slate-50 border-t p-4 sm:p-6 space-y-5">
                <h4 className="font-semibold text-gray-800 text-base">Leave Feedback</h4>
                <div className="flex items-center gap-6 text-gray-600">
                    <button onClick={handleLike} disabled={isLiked} className="flex items-center gap-2 text-base font-semibold hover:text-blue-600 disabled:text-blue-600 disabled:cursor-not-allowed transition-colors">
                        {isLiked ? <LikeFilled className="text-xl" /> : <LikeOutlined className="text-xl" />}
                        <span>{likes}</span>
                    </button>
                    <div className="flex items-center gap-2 text-base font-semibold">
                        <MessageOutlined className="text-xl" />
                        <span>{comments.length}</span>
                    </div>
                </div>
                {comments.length > 0 && (
                    <div className="space-y-4 max-h-52 overflow-y-auto pr-2">
                        {comments.map((comment) => (
                            <div key={comment._id} className="flex items-start gap-3">
                                <Avatar size="small" className="bg-gray-200 mt-1"><i className="fa-solid fa-user text-gray-500"></i></Avatar>
                                <div className="bg-white border rounded-lg p-3 text-sm flex-grow">
                                    <p className="text-gray-800 leading-relaxed">{comment.content}</p>
                                    <p className="text-xs text-gray-400 mt-1.5 text-right">{moment(comment.createdAt).fromNow()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-start gap-3 pt-4 border-t border-gray-200">
                    <Avatar><i className="fa-solid fa-user"></i></Avatar>
                    <TextArea placeholder="Add a comment..." autoSize={{ minRows: 1, maxRows: 4 }} value={commentText} onChange={(e) => setCommentText(e.target.value)} />
                    <Button type="primary" onClick={handleCommentSubmit} loading={isSubmitting}>Send</Button>
                </div>
            </div>
        </div>
    );
};

// --- Main View Component ---
const SharedLinkView = () => {
    const { shareableId } = useParams();
    const [linkData, setLinkData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [requiresPassword, setRequiresPassword] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const fetchLinkData = useCallback(async (isInitialLoad = false) => {
        if (isInitialLoad) setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_API}/api/links/public/${shareableId}`);
            if (response.data.requiresPassword) {
                setRequiresPassword(true);
                setLinkData(prev => ({ ...prev, title: response.data.title, posts: [], profileDetails: {} }));
            } else {
                setLinkData(response.data);
                if (isInitialLoad) document.title = `${response.data.title} - Oolook Preview`;
            }
        } catch (err) {
            if (isInitialLoad) setError(err.response?.data?.message || 'The link is invalid or has been deleted.');
            else console.error("Failed to refresh link data, showing stale data.", err);
        } finally {
            if (isInitialLoad) setLoading(false);
        }
    }, [shareableId]);

    const handleVerifyPassword = async (password) => {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_API}/api/links/public/${shareableId}/verify`, { password });
        setLinkData(response.data);
        setRequiresPassword(false);
        setIsAuthenticated(true); 
        document.title = `${response.data.title} - Oolook Preview`;
    };
    
    const handleInteraction = useCallback((postId, type, data) => {
        setLinkData(currentData => {
            if (!currentData || !currentData.posts) return currentData;
            const newPosts = currentData.posts.map(post => {
                if (post._id === postId) {
                    const updatedPost = { ...post };
                    if (type === 'like') updatedPost.likes = (updatedPost.likes || 0) + 1;
                    if (type === 'comment' && data) updatedPost.comments = [data, ...(updatedPost.comments || [])];
                    return updatedPost;
                }
                return post;
            });
            return { ...currentData, posts: newPosts };
        });
    }, []);

    useEffect(() => {
        document.title = "Oolook - Content Preview";
        fetchLinkData(true);
    }, [shareableId, fetchLinkData]);

    if (loading) return <div className="flex justify-center items-center h-screen bg-gray-50"><Spin size="large" /></div>;
    if (error) return (
        <div className="flex justify-center items-center h-screen bg-gray-50 p-4 text-center">
            <div>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-6">Oolook</h2>
                <Empty description={<span className="text-red-500 font-semibold">{error}</span>}>
                    <Button type="primary" href="/login">Go to Oolook Login</Button>
                </Empty>
            </div>
        </div>
    );
    if (requiresPassword && !isAuthenticated) return <PasswordForm title={linkData?.title} onVerify={handleVerifyPassword} />;
    if (!linkData || !linkData.posts) return <div className="flex justify-center items-center h-screen bg-gray-50"><Spin size="large" /></div>;

    return (
        <div className="min-h-screen bg-gradient-to-b from-purple-50 via-blue-50 to-white">
            <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12">
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl shadow-md border p-8 text-center mb-10">
                            <h1 className="text-4xl font-extrabold text-gray-900">{linkData.title}</h1>
                            <p className="text-gray-600 mt-2 text-lg">{linkData.posts?.length || 0} post(s) for review</p>
                        </div>
                        {linkData.posts && linkData.posts.length > 0 ? (
                            <div className="space-y-12">
                                {linkData.posts.map(post => (
                                    <PublicPostCard 
                                        key={post._id} 
                                        post={post} 
                                        shareableId={shareableId} 
                                        onInteraction={handleInteraction}
                                        profileDetails={linkData.profileDetails || {}}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white p-12 rounded-lg border">
                               <Empty description="No posts have been added to this shareable link yet." />
                            </div>
                        )}
                    </div>

                    <div className="hidden lg:block">
                        <CtaPanel />
                    </div>
                </div>
            </main>
            
            <footer className="text-center py-8 text-sm text-gray-500 mt-16">
                <p>&copy; {new Date().getFullYear()} Oolook. All Rights Reserved.</p>
            </footer>
        </div>
    );
};

export default SharedLinkView;